(ns app.front.view.inspections.visit
  (:require [app.domain.booking.model :as booking-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.domain.verdict.common-util :as verdict-util]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.actions :as ui-actions]
            [app.front.ui.notes :as ui-notes]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.view.attachment.list :as attachment-list]
            [app.front.view.attachment.subs :as attachment-subs]
            [app.front.view.inspections.booking-dialog :as booking-dialog]
            [app.front.view.inspections.checklists.checklists :as checklists]
            [app.front.view.inspections.checklists.checklists-v2 :as checklists-v2]
            [app.front.view.inspections.deficiencies-v2 :as deficiencies-v2]
            [app.front.view.inspections.event-list :as inspection-event-list]
            [app.front.view.inspections.my-today :as inspection-my-today]
            [app.front.view.inspections.photos-section :as photo-section]
            [app.front.view.inspections.subs :as inspection-subs]
            [app.front.view.inspections.time-tracking :as time-tracking]
            [app.front.view.inspections.visit-result :as visit-result]
            [app.front.view.workspace.subs :as ws-subs]
            [app.front.view.workspace.util :as ws-util]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.user.model :as user-model]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-event-fx
  ::initialize
  (fn [{:keys [db]} [_ workspace-id inspection-id]]
    {:db       (assoc db ::current-inspection-id inspection-id)
     :dispatch [::deficiencies-v2/initialize-view workspace-id inspection-id]}))

(rf/reg-sub ::state :-> ::state)


(rf/reg-event-db
  ::files-loaded
  (fn [db _]
    (assoc db ::loading-files? false)))


(rf/reg-sub ::loading-files? :-> ::loading-files?)


(rf/reg-event-fx ::get-files
  (fn [{:keys [db]} [_ inspection-id]]
    {:comm/command {:command          :inspection/get-entities
                    :method           :get
                    :data             {:inspection-id inspection-id}
                    :on-success       [::get-files.success]
                    :finally-dispatch [::files-loaded]}
     :db           (assoc db ::loading-files? true
                          ::state {})}))

(rf/reg-event-fx ::get-files.success
  (fn [{:keys [db]} [_ status {:keys [entities unfinished-import-session-id]}]]
    {:db       (-> db
                   (assoc-in [::state :unfinished-import-session-id] unfinished-import-session-id))
     :dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]}))


(rf/reg-sub ::inspection-id
  :-> ::current-inspection-id)


(rf/reg-sub ::current-inspection
  :<- [::inspection-id]
  :<- [::entities/context]
  (fn [[inspection-id ctx] _]
    (entities/find-entity ctx inspection-id)))


(rf/reg-sub ::unfinished-import-session-id
  :<- [::state]
  :-> :unfinished-import-session-id)


(rf/reg-sub ::import-session-href
  :<- [::ws-subs/authority-group-id]
  :<- [::unfinished-import-session-id]
  (fn [[group-id session-id] _]
    (when session-id
      (href/href :view/group.admin.import.session
                 (util/mmap group-id session-id)))))


(rf/reg-sub ::loading?
  :<- [::ws-subs/loading?]
  :<- [::loading-files?]
  :<- [::current-inspection]
  (fn [[ws-loading? loading-files? inspection]]
    (or ws-loading?
        loading-files?
        (nil? inspection))))


(rf/reg-sub ::inspection-name
  :<- [::current-inspection]
  :<- [:language]
  (fn [[inspection lang] _]
    (inspection-model/inspection-name inspection lang)))


(rf/reg-sub ::children-of-current-inspection
  :<- [::current-inspection]
  :-> :entity/_parent)


(rf/reg-sub ::all-bookings
  :<- [::children-of-current-inspection]
  (fn [children _]
    (->> children
         (filter booking-model/is-booking?))))


(defn booking-of? [visit booking]
  (= (:db/id booking)
     (-> visit :inspection.visit/booking :db/id)))


(defn pending-booking? [booking]
  (-> booking
      :domain/state
      #{:booking.state/cancelled :booking.state/fulfilled}
      not))


(defn starts-today-or-before? [{:booking/keys [actual-start-time]}]
  (when actual-start-time
    (date-util/on-or-before? actual-start-time (date-util/today-end-of-day))))


(rf/reg-sub ::latest-booking
  :<- [::all-bookings]
  :<- [::ongoing-visit]
  :<- [:session/user]
  (fn [[bookings visit user] _]
    (let [my-user-id (:db/id user)]
      (->> bookings
           (filter (if (and visit (-> visit :entity/creator :db/id (= my-user-id)))
                     (partial booking-of? visit)
                     pending-booking?))
           (filter starts-today-or-before?)
           (filter #(-> % :booking/assignee :db/id (= my-user-id)))
           (sort-by :booking/requested-start-time >)
           first))))


(rf/reg-sub ::latest-booking-id
  :<- [::latest-booking]
  (fn [{id :db/id} _] id))


(rf/reg-sub ::all-visits
  :<- [::children-of-current-inspection]
  (fn [children _]
    (->> children
         (filter inspection-model/is-visit?))))


(rf/reg-sub ::ongoing-visit
  :<- [::all-visits]
  (fn [all-visits _]
    (->> all-visits
         (filter #(-> %
                      :domain/state
                      (= :inspection.visit.state/in-progress)))
         first)))


(rf/reg-sub ::recurrence
  :<- [::ongoing-visit]
  (fn [ongoing-visit _]
    @(rf/subscribe [::visit-result/recurrence-values (:db/id ongoing-visit)])))


(rf/reg-sub ::end-visit-disabled?
  (fn [[_]]
    [(rf/subscribe [::ongoing-visit])
     (rf/subscribe [::time-tracking/enabled?])
     (rf/subscribe [::recurrence])
     (rf/subscribe [::booking-dialog/booking-date-time-slot])])
  (fn [[visit time-tracking? recurrence booking-date-time-slot] _]
    (boolean (or (nil? (:inspection.visit/visit-result visit))
                 (and time-tracking?
                      (nil? (:inspection.visit/time-spent visit)))
                 (and (:create-inspection? recurrence)
                      (not (:time-slot-id booking-date-time-slot)))
                 (inspection-model/mandatory-visit-result-detail-missing? visit)))))


(rf/reg-sub ::view-data
  :<- [:session/authority-user?]
  :<- [:session/inspector-user?]
  :<- [::import-session-href]
  :<- [::ws-subs/href]
  (fn [[auth-user? inspector? import-session-href ws-href] _]
    {:back-href (cond
                  (and auth-user? import-session-href) import-session-href
                  inspector? (href/href :view/my-inspections)
                  :else ws-href)}))


(def no-content :div.text-gray-700.font-bold.uppercase.mb-4)


(defn booked-by [{:keys [tr]} {:user/keys [mobile-phone] :as user}]
  [:div.flex.flex-wrap
   [:div.mr-6
    [:label.bx--label.bold (tr [:booking/requested-by])]
    [:div (user-model/full-name user)]]
   [:div
    [:label.bx--label.bold (tr [:phone-number])]
    [:div mobile-phone]]])


(defn project-href [project]
  (href/href :view/project
             {:project-id (:db/id project)}))


(defn booking-summary [{:keys [tr] :as props}
                       {:booking/keys [notes dispatch-notes]
                        :entity/keys  [creator]
                        :as           booking}
                       code-enforcement?]
  [:<>
   (if booking
     [ui-notes/internal-external-note
      {:internal-title (tr [:booking/dispatch-notes])
       :external-title (tr [:booking/notes-from-booking])
       :internal       dispatch-notes
       :external       notes
       :tr             tr}]
     [no-content (tr [:booking/does-not-exist])])
   (when (and creator
              (not code-enforcement?))
     [booked-by
      props
      ;; :entity/creator is exported differently from other entities,
      ;; need to re-subscribe to the real entity
      (<sub [::entities/entity (:db/id creator)])])])


(defn get-permit-desc [workspace]
  (some-> workspace
          workspace-model/workspace-application
          verdict-model/single-verdict
          verdict-util/get-permit-description-value
          str-util/blank->nil))


(defn general-info-section [{:keys [tr inspection workspace]
                             :as   props}]
  (let [permit-description           (get-permit-desc workspace)
        case-description             (:workspace/case-description workspace)
        project                      (workspace-model/project workspace)
        ws-id                        (:workspace/municipal-case-id workspace)
        lang                         (<sub [:language])
        inspection-priority-enabled? (<sub [::ff/enabled? :feature-flag.id/inspection-priority])
        date-fn                      (<sub [::date-util/date-fn])
        authority-group              (inspection-model/authority-group inspection)
        code-enforcement?            (-> workspace :workspace/permit-type (= :permit-type/CE))]
    [section/section
     "inspection-general-section"
     props
     :inspection.visit/general-information
     [:div.flex.flex-wrap.mb-6
      (when (:workspace/primary-property workspace)
        [:div.mr-6
         [:label.bx--label (tr [:inspection/address-title])]
         [:div
          (cc/link
            {:href       (ws-util/workspace-href workspace)
             :class-name "bold"
             :aria-label (tr [:inspection.visit/link-to-application])}
            (inspection-my-today/whole-address workspace tr lang))]])
      (when project
        [:div
         [:label.bx--label (tr [:inspection/project])]
         [:div
          (cc/link
            {:href       (project-href project)
             :class-name "bold"
             :aria-label (tr [:inspection.visit/link-to-project])}
            (:project/name project))]])]

     [ui-notes/note-block
      (tr [:inspection/description])
      (inspection-model/inspection-description inspection lang)]
     (when (and (<sub [::ff/enabled? :feature-flag.id/inspection-created-due-date])
                (or (:inspection/created inspection)
                    (:inspection/due-date inspection)))
       [:div.flex.flex-wrap.mb-6
        (when-let [created (:inspection/created inspection)]
          [:div.mr-6
           [:label.bx--label (tr [:inspection/created])]
           [:div (date-fn created)]])
        (when-let [due-date (:inspection/due-date inspection)]
          [:div
           [:label.bx--label (tr [:inspection/due-date-visit])]
           [:div (date-fn due-date)]])])

     (when (or permit-description case-description ws-id)
       [:div.flex.flex-wrap.mb-6
        (when ws-id
          [:div.mr-6
           [:label.bx--label (tr [:workspace/municipal-case-id])]
           [:div (:workspace/municipal-case-id workspace)]])
        (when permit-description
          [:div
           [:label.bx--label (tr [:inspection.visit/permit-description])]
           [:div [ui-rt/editor-legacy {:initial-value permit-description
                                       :id            "permit-description"}]]])
        (when case-description
          [:div
           [:label.bx--label (tr [:case/description-title])]
           [:div [ui-rt/editor-legacy {:initial-value case-description
                                       :id            "case-description"}]]])])
     (when (and inspection-priority-enabled?
                (group-model/has-feature? authority-group :group.feature/inspection-priority))
       [:div.my-4
        (into [ui/select-pills {:label-text (tr [:inspection/priority])
                                :value      (-> inspection :inspection/priority)
                                :disabled?  true
                                :id         "priority-selector"}]
              (for [priority inspection-model/inspection-priorities]
                {:value priority
                 :text  (tr [priority])}))])

     [booking-summary props (<sub [::latest-booking]) code-enforcement?]]))

(defn attachments-section [{:keys [execution application target-entity tr] :as props}]
  (let [related-targets          (workspace-model/get-related-targets target-entity)
        application-attachments? (and application (<sub [::attachment-subs/has-attachments? (:db/id application)]))
        execution-attachments?   (and execution (<sub [::attachment-subs/has-attachments? (:db/id execution)]))]
    (when (or application-attachments? execution-attachments?)
      [section/section
       "attachments-section"
       (assoc props :initially-collapsed? true)
       :view/application-attachments
       [:<>
        [attachment-list/filters {:target-ids (->> related-targets
                                                   (concat [application execution])
                                                   (map :db/id))}]

        (when application-attachments?
          [attachment-list/attachment-table {:process-target-id (:db/id application)
                                             :workspace-id      (-> application :entity/parent :workspace/id)
                                             :title             (tr [:workspace.state/application])
                                             :editable?         false}])
        (when execution-attachments?
          [attachment-list/attachment-table {:process-target-id (:db/id execution)
                                             :workspace-id      (-> execution :entity/parent :workspace/id)
                                             :title             (tr [:workspace.state/execution])
                                             :editable?         false}])

        [attachment-list/related-workspace-attachments {:targets related-targets}]]])))

(defn action-button [{:keys [inspection-id signal target]}]
  [ui-actions/signal-button
   {:tr            (<sub [::ws-subs/tr])
    :button-opts   {:size :small}
    :target-entity target
    :signal        [signal
                    (p/assoc-if {:inspection-id inspection-id}
                                :booking-id (<sub [::latest-booking-id]))]}])

(defn top-bar-actions []
  (let [tr            (<sub [::ws-subs/tr])
        inspection    (<sub [::current-inspection])
        target        (:entity/parent inspection)
        visit         (<sub [::ongoing-visit])
        inspection-id (:db/id inspection)
        user-id       (<sub [:session/user-id])]
    [:div.flex.flex-wrap.justify-between
     [:div.inline-block
      (cond
        (<sub [::process/signal-permitted?
               target
               [:process.signal/reopen-inspection {:inspection-id inspection-id}]])
        [ui-actions/signal-button
         {:tr             tr
          :button-opts    {:size :small}
          :target-entity  target
          :signal         [:process.signal/reopen-inspection {:inspection-id inspection-id}]
          :confirm?       true
          :confirm-params {:reason {:component ui/text-area
                                    :opts      {:label-text (tr [:inspection/reason-for-reopening])
                                                :required   true}}}}]

        (and visit
             (-> visit
                 :inspection.visit/inspectors
                 du/entity-id-set
                 (contains? user-id)
                 not)
             (<sub [::process/signal-permitted?
                    target
                    [:process.signal/join-inspection-visit {:inspection-id inspection-id}]]))
        [action-button (util/mmap {:signal :process.signal/join-inspection-visit}
                                  inspection-id
                                  target)]

        (and (nil? visit)
             (<sub [::process/signal-permitted?
                    target
                    [:process.signal/start-inspection-visit {:inspection-id inspection-id
                                                             :booking-id    (<sub [::latest-booking-id])}]]))
        [action-button (util/mmap {:signal :process.signal/start-inspection-visit}
                                  inspection-id
                                  target)])]]))

(defn view [{{{:keys [workspace-id]} :path} :parameters}]
  (let [workspace     (<sub [::entities/entity {:workspace/id workspace-id}])
        tr            (<sub [::ws-subs/tr])
        auth-group    (-> workspace :domain/authority-group)
        inspection    (<sub [::current-inspection])
        execution     (workspace-model/workspace-execution workspace)
        application   (workspace-model/workspace-application workspace)
        target-entity (:entity/parent inspection)
        visit         (<sub [::ongoing-visit])
        phrases       (<sub [::entities/entities {:entity/type            :entity.type/phrase
                                                  :domain/authority-group (:db/id auth-group)}])
        booking-id    (<sub [::latest-booking-id])
        props         (util/mmap tr inspection target-entity workspace phrases auth-group execution application booking-id visit)]
    (if (<sub [::loading?])
      [:div.flex.w-full.justify-center
       (cc/loading-spinner {:with-overlay false})]
      [:div.cloudpermit-page.workspace-page
       [ui/page-header-el
        [:h1.pl-8.mt-4
         (str (tr [:inspection/inspection])
              " - "
              (<sub [::inspection-name]))]]
       (when (du/in-state? :inspection.state/draft inspection)
         [ui/help (tr [:inspection/visit-preview-draft-inspection])])
       [general-info-section props]

       [attachments-section props]

       (when (<sub [::ws-subs/has-feature? :group.feature/inspection-checklists])
         (if (<sub [::inspection-subs/checklist-configuration-v2])
           [checklists-v2/checklists-section (assoc props :visit visit)]
           [checklists/checklists-section (assoc props :visit visit)]))

       (when (inspection-model/has-deficiencies? (:workspace/permit-type workspace))
         [deficiencies-v2/render-deficiency-list props])

       [photo-section/photos-videos-section (assoc props :visit visit)]

       [section/section
        "other-visits-section"
        props
        :inspection/other-visits
        [inspection-event-list/inspection-items
         {:tr                tr
          :separator-opts    {:label-class "bg-white"}
          :show-visit-files? true}
         inspection]]

       [visit-result/result-section
        (<sub [::end-visit-disabled?])
        (assoc props :visit visit)]

       ;; needed because one can edit the booking in "all visits" section
       [booking-dialog/render-booking-modal tr target-entity]])))
